// @givmo/board — one written consent: its text, who has to sign it, and who has.
//
// ══ WHAT THIS SCREEN IS RESPONSIBLE FOR GETTING RIGHT ═══════════════════════════════════════════
//
//   * §4 IS THE FIRST THING ON THE PAGE while the consent is short of unanimity. `ConsentStanding`
//     renders the backend's own sentence above the text, not below it and not in a footnote — a
//     partial consent must never read like a finished one.
//   * THE ROSTER SHOWN IS THE SNAPSHOT, not the current roster. Once circulated, who must sign is a
//     property of the CONSENT. A seat removed from the board afterwards is still on this list and still
//     required; a seat added afterwards is not on it and cannot sign. The page says so, because a
//     secretary looking at a name that is no longer on their roster will otherwise assume a bug.
//   * THE SIGNATURE LOG IS APPEND-ONLY AND IS SHOWN AS ONE. A withdrawal does not remove the signature
//     it withdrew; both acts are listed, in order, with who captured each and how. That log is the §1.2
//     provability set, and hiding a superseded act would defeat the reason it is kept.
//   * THE TWO CAPTURE CLASSES STAY DISTINGUISHABLE. A director's own act and an officer's on-behalf
//     entry never render with the same label, and the officer's entry always shows its provenance note.
//   * WHAT THE EMAIL DID, AND NOTHING MORE (CBG-OPS1-CIRC-WEB). Each seat says in one quiet line
//     whether the circulation's email went (the frame `k2-roster`). "Emailed" means the mail service
//     accepted it, never that anyone read it; every reason it didn't go reads as the officer's one act,
//     asked only while it can still be done (the consent out for signatures, the seat not signed);
//     "Hasn’t joined Board yet" is said only while the live seat is still unjoined, read from the
//     roster as it is now. A consent circulated before the email existed says nothing. The delivery is
//     working state beside the consent, never part of its record.
//
// A CONSENT THAT APPROVES MINUTES (CBG-RC9) says what it approves above its text — which minutes,
// the way to them, and once it went out the fingerprint its text names them by — and, once executed,
// what happened to them: approved, or not, and why, in the officer's own words beside the next step
// (the frames `rc9-circulate`, `rc9-consent-refused`).
//
// THE FILED RECORD IS A LINK. An executed consent is filed as a `written_consent` board record through
// the record core's own law, and `record_id` names it. Until the minute book existed this was rendered
// as a bare reference — a link to a route that did not exist would have been worse than none — and it
// now resolves to `/records/$recordId`, where the record's own approval evidence and event log live.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Fingerprint } from '../../components/fingerprint';
import { Link, useParams } from '../../board-routing';
import {
  isGivmoError,
  type BoardConsentRosterMemberOut,
  type OrgBoardWrittenConsentOut,
} from '@givmo/api-client';
import {
  Badge,
  Button,
  Card,
  CardBody,
  ErrorMessage,
  Skeleton,
  TextField,
  Textarea,
  toast,
} from '@givmo/design-system';
import { ConnectedAiDraftBadge } from '../../components/connected-ai-draft-badge';
import { PageShell } from '../../components/page-shell';
import { useBoardHost } from '../../board-host';
import { hasBoardPermission } from '../../board-host';
import { ConsentStanding } from './consent-standing';
import { CirculateDialog } from './circulate-dialog';
import { termNoteWords } from '../roster/term-note-words';
import { ReviseDialog } from './revise-dialog';
import { WithdrawDialog } from './withdraw-dialog';
import { RecordedAssentDialog, type RecordedAssentMode } from './recorded-assent-dialog';
import { OfficerStatementPanel } from './officer-statement-panel';
import { consentsQueryKey } from './consents-page';
import {
  actsForSeat,
  consentStatusLabel,
  consentStatusTone,
  displayDate,
  displayInstant,
  isAwaitingSignatures,
  isDraft,
  isSignatureAct,
  seatDelivery,
  seatRoleLabel,
  seatsStillUnjoined,
  signatureLogLine,
  consentCommitteeName,
} from './consents-decode';
import { committeeMeetingsWords as cm } from '../committees/committee-meetings-words';
import { useBoardOperations } from '../../board-runtime';
import { extractBoardMembers } from '../roster/roster-decode';
import { consentDeliveryWords as circ } from './consent-delivery-words';
import { consentMinutesSets, type ConsentMinutes } from './minutes-consent-decode';
import { minutesConsentWords as rc9 } from './minutes-consent-words';
import {
  closedSessionsAmong,
  minutesSetsWords as several,
  setsInASentence,
} from './minutes-sets-words';
// CBG-YR3-ELECT: an executed consent's election result, put on the roster by the officer's own act.
import { RecordResultDialog } from '../in-meeting/record-result-dialog';
import { termsWords as tw } from '../roster/terms-words';
import { displayDate as calendarDate, todayAsDateInput } from '../../lib/display';
// CBG-YR4-PAY-CONSENT: the consent's compensation details and the block its text carries.
import { ConsentCompensationPanel } from '../compensation/consent-compensation-panel';

/** A link set on its own line. */
const LINK =
  'font-sans text-s13 font-w600 tracking-givmo text-brand-600 outline-none transition-colors hover:text-brand-700 focus-visible:underline';

interface AssentTarget {
  mode: RecordedAssentMode;
  boardMemberId: string;
  seatName: string;
}

export function ConsentDetailPage() {
  const operations = useBoardOperations();
  const { consentId } = useParams({ from: '/authenticated/consents/$consentId' });
  const queryClient = useQueryClient();
  const { permissions, orgRef, isReady } = useBoardHost();
  const canManage = hasBoardPermission(permissions, 'board_consent:manage');
  // A draft's wording is edited through the draft door — the backend's `board_record:write`.
  const canEditDraft = hasBoardPermission(permissions, 'board_record:write');

  const [circulating, setCirculating] = useState(false);
  const [revising, setRevising] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const [assent, setAssent] = useState<AssentTarget | null>(null);
  const [editing, setEditing] = useState(false);
  const canManageRoster = hasBoardPermission(permissions, 'board_member:manage');
  const [recordingResult, setRecordingResult] = useState(false);

  const consentKey = ['board-written-consent', orgRef, consentId] as const;
  const consentQuery = useQuery({
    queryKey: consentKey,
    enabled: isReady && orgRef !== null,
    queryFn: async (): Promise<OrgBoardWrittenConsentOut> => {
      const { data } = await operations.officerGetBoardWrittenConsent({
        path: { org_ref: orgRef ?? '', consent_id: consentId },
        throwOnError: true,
      });
      return data;
    },
  });

  const refreshAll = async () => {
    await queryClient.invalidateQueries({ queryKey: consentKey });
    await queryClient.invalidateQueries({ queryKey: consentsQueryKey(orgRef) });
  };

  const consent = consentQuery.data ?? null;
  // CBG-RC9-MULTI (§M9): every set it names, in its order — one, for a consent naming one.
  const approvesSets = consentMinutesSets(consent);
  // CBG-RC9 — the minutes this consent approves, when it names some (its first set: the read's list
  // alone since CBG-RC9-MULTI-CONTRACT), and — for a draft, the one state that goes out — their
  // meeting's title for the sentence that names them before it does.
  const approves = approvesSets[0] ?? null;
  const approvesMeetingId = approves?.meetingId ?? null;
  const approvesMeetingQuery = useQuery({
    ...operations.officerGetBoardMeetingOptions({
      path: { org_ref: orgRef ?? '', meeting_id: approvesMeetingId ?? '' },
    }),
    enabled:
      isReady && orgRef !== null && approvesMeetingId !== null && isDraft(consent?.status ?? null),
  });

  // CBG-OPS1-CIRC-WEB — the roster as it is NOW, read only where some seat's email went while no
  // account had joined it: whether that seat has joined since decides "Hasn’t joined Board yet"
  // (the orchestrator's ruling, 2026-10-14). Until it answers, or if it fails, nothing is said.
  const mailedUnjoined = (consent?.roster ?? []).some((seat) => {
    const outcome = seatDelivery(seat);
    return outcome?.state === 'sent' && outcome.unjoinedThen;
  });
  const liveSeatsQuery = useQuery({
    ...operations.officerListBoardMembersOptions({ path: { org_ref: orgRef ?? '' } }),
    enabled: isReady && orgRef !== null && mailedUnjoined,
    select: (body: unknown) => extractBoardMembers(body),
  });
  const stillUnjoined = seatsStillUnjoined(liveSeatsQuery.isSuccess ? liveSeatsQuery.data : null);
  // CBG-YR3-ELECT: the roster the result dialog picks from, read when it opens.
  const rosterQuery = useQuery({
    ...operations.officerListBoardMembersOptions({ path: { org_ref: orgRef ?? '' } }),
    enabled: isReady && orgRef !== null && recordingResult,
    select: (body: unknown) => extractBoardMembers(body),
  });

  if (consentQuery.isPending) {
    return (
      <PageShell title="Written consent" testId="page-consent-detail">
        <Skeleton className="h-90 w-full rounded-lg" testId="consent-detail-loading" />
      </PageShell>
    );
  }

  if (consentQuery.isError || consent === null) {
    return (
      <PageShell title="Written consent" testId="page-consent-detail">
        <Card testId="consent-detail-error">
          <CardBody className="flex flex-col items-start gap-12">
            <p className="font-sans text-s15 font-w400 leading-h15 tracking-givmo text-text-secondary">
              We couldn’t open this consent.
            </p>
            <Button
              appearance="saas"
              tone="secondary"
              onClick={() => void consentQuery.refetch()}
              testId="consent-detail-retry"
            >
              Try again
            </Button>
          </CardBody>
        </Card>
      </PageShell>
    );
  }

  const version = consent.version ?? null;
  const roster = consent.roster ?? [];
  const signatures = consent.signatures ?? [];
  const draft = isDraft(consent.status);
  const awaiting = isAwaitingSignatures(consent.status);
  const org = orgRef ?? '';
  // CBG-YR6-CONSENT: a committee's written consent says whose it is, and goes to its voting members.
  const committee = consentCommitteeName(consent);

  return (
    <PageShell
      title={version?.title ?? 'Written consent'}
      description={
        committee !== null
          ? `${cm.consentOf(committee)} · Dated ${displayDate(consent.consent_date)}`
          : `Dated ${displayDate(consent.consent_date)}`
      }
      actions={
        <Link
          to="/consents"
          // Exact: this page sits beneath that address, and it is not the current page.
          exact
          className="font-sans text-s14 font-w600 tracking-givmo text-brand-600 outline-none transition-colors hover:text-brand-700 focus-visible:underline"
          data-testid="consent-detail-back"
        >
          All written consents
        </Link>
      }
      testId="page-consent-detail"
    >
      <div className="flex flex-wrap items-center gap-8">
        <Badge tone={consentStatusTone(consent.status)}>{consentStatusLabel(consent.status)}</Badge>
        {version !== null ? <Badge tone="neutral">Version {version.version_number}</Badge> : null}
        <ConnectedAiDraftBadge source={version} testId="consent-detail-connected-ai-draft" />
      </div>

      <ConsentStanding
        statement={consent.standing_statement}
        isAnActOfTheBoard={consent.is_an_act_of_the_board === true}
        status={consent.status}
        signedCount={consent.signed_count}
        requiredCount={consent.required_count}
        testId="consent-detail-standing"
      />

      {approvesSets.length > 1 ? (
        <ApprovesSeveralSets sets={approvesSets} draft={draft} />
      ) : approves !== null ? (
        <ApprovesMinutes approves={approves} draft={draft} />
      ) : null}

      {consent.status?.toLowerCase() === 'withdrawn' && consent.withdrawn_reason ? (
        <Card testId="consent-detail-withdrawn">
          <CardBody className="flex flex-col gap-6">
            <span className="font-sans text-s13 font-w600 tracking-givmo text-text-strong">
              Why it was stopped
            </span>
            <span className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
              {consent.withdrawn_reason}
            </span>
            <span className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary">
              Stopped {displayInstant(consent.withdrawn_at)}
            </span>
          </CardBody>
        </Card>
      ) : null}

      {/* ── The text ──────────────────────────────────────────────────────────────────────────── */}
      {draft && editing && version !== null ? (
        <DraftEditor
          orgRef={org}
          consentId={consentId}
          title={version.title}
          body={version.body}
          consentDate={consent.consent_date ?? ''}
          onCancel={() => setEditing(false)}
          onSaved={async () => {
            setEditing(false);
            await refreshAll();
          }}
        />
      ) : (
        <Card testId="consent-detail-text">
          <CardBody className="flex flex-col gap-12">
            <div className="flex flex-wrap items-start justify-between gap-12">
              <h2 className="font-sans text-s15 font-w600 tracking-givmo text-text-strong">
                What the consent says
              </h2>
              {draft && canEditDraft ? (
                <Button
                  appearance="saas"
                  tone="secondary"
                  size="sm"
                  onClick={() => setEditing(true)}
                  testId="consent-detail-edit"
                >
                  Edit the wording
                </Button>
              ) : null}
            </div>

            {/* A 64-character fingerprint has no break, so the text wraps anywhere rather than run
                past its card on a phone. */}
            <pre
              className="whitespace-pre-wrap [overflow-wrap:anywhere] font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary"
              data-testid="consent-detail-body"
            >
              {version?.body ?? ''}
            </pre>

            {version !== null && version.content_hash ? (
              // The WHOLE fingerprint, selectable and copyable (CBG-UX4): a shortened one cannot be
              // compared against a signature or a later copy.
              <div className="flex flex-col gap-6" data-testid="consent-detail-address">
                <span className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
                  Every signature names this exact wording, by its fingerprint
                </span>
                <Fingerprint
                  value={version.content_hash}
                  // A consent that approves minutes shows their fingerprint too: each copy button
                  // says whose it is (CBG-UX4).
                  label={approves !== null ? rc9.wordingFingerprintLabel : undefined}
                  testId="consent-detail-hash"
                />
                <span className="font-sans text-s12 font-w400 leading-h15 tracking-givmo text-text-tertiary">
                  Change a word and the fingerprint changes with it.
                </span>
              </div>
            ) : null}
          </CardBody>
        </Card>
      )}

      {/* ── Its compensation details (CBG-YR4-PAY-CONSENT) ──────────────────────────────────────── */}
      {/* Whose pay the consent sets and the documents the board relied on, printed as ONE block in the
          text above, which the officer places; nothing of it draws where the backend serves none. */}
      {version !== null ? (
        <ConsentCompensationPanel
          consentId={consentId}
          committee={Boolean(consent.committee_id)}
          body={version.body}
          textBeingEdited={draft && editing}
        />
      ) : null}

      {/* ── Who has to sign ───────────────────────────────────────────────────────────────────── */}
      {roster.length > 0 ? (
        <Card testId="consent-detail-roster">
          <CardBody className="flex flex-col gap-12">
            <h2 className="font-sans text-s15 font-w600 tracking-givmo text-text-strong">
              Who has to sign
            </h2>
            <p className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary">
              This is your board as it stood when the consent went out, kept exactly as it was.
              Anyone who joined afterwards isn’t on it and can’t sign this one; anyone who left is
              still one of the signatures it needs.
            </p>

            <ul className="flex flex-col gap-12">
              {roster.map((seat) => {
                const acts = actsForSeat(signatures, seat.id);
                const signed = seat.has_signed === true;
                return (
                  <li
                    key={seat.id}
                    className="flex flex-col gap-6 border-t border-border-subtle pt-12 first:border-t-0 first:pt-0"
                    data-testid={`consent-seat-${seat.board_member_id}`}
                  >
                    <div className="flex flex-wrap items-start justify-between gap-10">
                      <div className="flex min-w-0 flex-col gap-2">
                        <span className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">
                          {seat.display_name}
                        </span>
                        <span className="font-sans text-s13 font-w400 tracking-givmo text-text-tertiary">
                          {seatRoleLabel(seat.board_role)} · {seat.email_of_record}
                        </span>
                        <DeliveryLine
                          seat={seat}
                          stillUnjoined={stillUnjoined.has(seat.board_member_id)}
                          stillToSign={awaiting && !signed}
                        />
                        {seat.term_had_ended === true ? (
                          // A recorded fact, and explicitly NOT a bar: plan §4 is clear that a lapsed
                          // term does not stop a director acting. Stated as what was recorded.
                          <span
                            className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary"
                            data-testid={`consent-seat-term-${seat.board_member_id}`}
                          >
                            {termNoteWords.termYouRecordedEnded(displayDate(seat.term_end_on))}
                          </span>
                        ) : null}
                      </div>
                      <Badge tone={signed ? 'success' : 'warning'}>
                        {signed ? 'Signed' : 'Not yet'}
                      </Badge>
                    </div>

                    {acts.length > 0 ? (
                      <ul
                        className="flex flex-col gap-6 pl-12"
                        data-testid={`consent-seat-log-${seat.board_member_id}`}
                      >
                        {acts.map((act) => (
                          <li key={act.id} className="flex flex-col gap-2">
                            <span
                              className="font-sans text-s13 font-w600 tracking-givmo text-text-secondary"
                              data-testid={`consent-act-${act.id}`}
                            >
                              {/* One line per act and capture, composed in one place (CBG-UX4): an
                                  act word beside a capture phrase said the verb twice, or
                                  contradicted it on a withdrawal. */}
                              {signatureLogLine(act)}
                            </span>
                            <span className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary">
                              {displayInstant(act.occurred_at)} · {act.identity_assertion}
                            </span>
                            {/* The wording's own block above already gives the fingerprint every
                                signature names, so an act that names THAT wording says so in one
                                line; only an act naming a different wording shows the whole block
                                (CBG-UX4). Every writer refuses an act whose echo differs from the
                                version in force, and this page lists that version's acts only, so
                                the second branch is a guard, not a path. */}
                            {act.content_hash &&
                            version !== null &&
                            act.content_hash === version.content_hash ? (
                              <span
                                className="font-sans text-s12 font-w400 leading-h15 tracking-givmo text-text-tertiary"
                                data-testid={`consent-act-address-${act.id}`}
                              >
                                It names this exact wording.
                              </span>
                            ) : act.content_hash ? (
                              <div
                                className="flex flex-col gap-4"
                                data-testid={`consent-act-address-${act.id}`}
                              >
                                <span className="font-sans text-s12 font-w400 leading-h15 tracking-givmo text-text-tertiary">
                                  It names a different wording, by this fingerprint
                                </span>
                                <Fingerprint
                                  value={act.content_hash}
                                  testId={`consent-act-hash-${act.id}`}
                                  label={`${seat.display_name}’s ${
                                    isSignatureAct(act) ? 'signature' : 'withdrawal'
                                  }`}
                                />
                              </div>
                            ) : null}
                            {act.provenance_note ? (
                              <span
                                className="font-sans text-s12 font-w400 leading-h15 tracking-givmo text-text-tertiary"
                                data-testid={`consent-act-provenance-${act.id}`}
                              >
                                Where it came from: {act.provenance_note}
                              </span>
                            ) : null}
                          </li>
                        ))}
                      </ul>
                    ) : null}

                    {awaiting && canManage ? (
                      <div className="flex flex-wrap gap-8 pl-12">
                        {signed ? (
                          <Button
                            appearance="saas"
                            tone="ghost"
                            size="sm"
                            onClick={() =>
                              setAssent({
                                mode: 'withdraw',
                                boardMemberId: seat.board_member_id,
                                seatName: seat.display_name,
                              })
                            }
                            testId={`consent-seat-withdraw-${seat.board_member_id}`}
                          >
                            {/* Whoever made the signature — the director in their own session or
                                an officer — this appends an officer-recorded withdrawal for the
                                seat, so it is worded like "Record their assent" (CBG-UX4). */}
                            Record their withdrawal
                          </Button>
                        ) : (
                          <Button
                            appearance="saas"
                            tone="secondary"
                            size="sm"
                            onClick={() =>
                              setAssent({
                                mode: 'record',
                                boardMemberId: seat.board_member_id,
                                seatName: seat.display_name,
                              })
                            }
                            testId={`consent-seat-record-${seat.board_member_id}`}
                          >
                            Record their assent
                          </Button>
                        )}
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </CardBody>
        </Card>
      ) : null}

      {/* ── The filed record ──────────────────────────────────────────────────────────────────── */}
      {consent.record_id ? (
        <>
          <Card testId="consent-detail-record">
            <CardBody className="flex flex-col gap-6">
              <span className="font-sans text-s15 font-w600 tracking-givmo text-text-strong">
                Filed in your minute book
              </span>
              <span className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
                Everyone signed, so this was filed as a record of your board on{' '}
                {displayInstant(consent.executed_at)}. It is part of your organization’s record from
                that moment, with every signature kept alongside it.
              </span>
              <Link
                to="/records/$recordId"
                params={{ recordId: consent.record_id }}
                className="font-sans text-s13 font-w600 tracking-givmo text-brand-600 outline-none transition-colors hover:text-brand-700 focus-visible:underline"
                data-testid="consent-detail-record-id"
              >
                Open it in your minute book
              </Link>
            </CardBody>
          </Card>
          <OfficerStatementPanel
            orgRef={org}
            recordId={consent.record_id}
            consentTitle={version?.title ?? 'Written consent'}
          />
          {/* CBG-YR3-ELECT (backend §7): if this consent elected anyone, the officer puts the result
              on the roster here — Board never reads the consent's text for names. */}
          {canManageRoster && consent.status === 'executed' ? (
            <Button
              appearance="saas"
              tone="secondary"
              className="self-start"
              onClick={() => setRecordingResult(true)}
              testId="consent-detail-record-result"
            >
              {tw.recordResult}
            </Button>
          ) : null}
        </>
      ) : null}

      {recordingResult && orgRef !== null ? (
        <RecordResultDialog
          orgRef={orgRef}
          record={{
            source: { consentId: consent.id },
            words: version?.title ?? 'Written consent',
            standing: tw.executed(calendarDate(consent.executed_at)),
            // The execution day on the reader's calendar: the prefills' start.
            day: consent.executed_at
              ? todayAsDateInput(new Date(consent.executed_at).getTime())
              : '',
          }}
          roster={rosterQuery.data ?? []}
          onClose={() => setRecordingResult(false)}
          onRecorded={() => setRecordingResult(false)}
        />
      ) : null}

      {/* ── What the officer can do next ──────────────────────────────────────────────────────── */}
      {/* Every act here is `board_consent:manage`: without it, the card is not offered. */}
      {(draft || awaiting) && canManage ? (
        <Card testId="consent-detail-actions">
          <CardBody className="flex flex-col gap-12">
            <h2 className="font-sans text-s15 font-w600 tracking-givmo text-text-strong">
              {draft
                ? 'When you’re ready'
                : committee !== null
                  ? cm.whileTheCommitteeSigns
                  : 'While your board is signing'}
            </h2>
            <div className="flex flex-wrap gap-10">
              {draft ? (
                <Button
                  appearance="saas"
                  tone="primary"
                  onClick={() => setCirculating(true)}
                  testId="consent-detail-circulate"
                >
                  {committee !== null ? cm.putToTheCommittee : 'Put it to the board'}
                </Button>
              ) : null}
              {awaiting ? (
                <>
                  <Button
                    appearance="saas"
                    tone="secondary"
                    onClick={() => setRevising(true)}
                    testId="consent-detail-revise"
                  >
                    Change the wording
                  </Button>
                  <Button
                    appearance="saas"
                    tone="danger"
                    onClick={() => setWithdrawing(true)}
                    testId="consent-detail-withdraw"
                  >
                    Stop it
                  </Button>
                </>
              ) : null}
            </div>
          </CardBody>
        </Card>
      ) : null}

      {circulating && version !== null ? (
        <CirculateDialog
          orgRef={org}
          consentId={consentId}
          consentTitle={version.title}
          consentDate={consent.consent_date ?? null}
          committeeId={consent.committee_id ?? null}
          committeeName={committee}
          // Every set it names, each as RC9 and RC11 name one (CBG-RC9-MULTI).
          approvesMinutes={
            approves === null
              ? null
              : setsInASentence(approvesSets, {
                  title: approvesMeetingQuery.data?.title,
                  date: approves.meetingDate,
                })
          }
          approvesSeveralSets={approvesSets.length > 1}
          onClose={() => setCirculating(false)}
          onCirculated={() => {
            setCirculating(false);
            void refreshAll();
          }}
        />
      ) : null}

      {revising && version !== null ? (
        <ReviseDialog
          orgRef={org}
          consentId={consentId}
          currentTitle={version.title}
          currentBody={version.body}
          signedCount={consent.signed_count ?? 0}
          committeeName={committee}
          onClose={() => setRevising(false)}
          onRevised={() => {
            setRevising(false);
            void refreshAll();
          }}
        />
      ) : null}

      {withdrawing && version !== null ? (
        <WithdrawDialog
          orgRef={org}
          consentId={consentId}
          consentTitle={version.title}
          onClose={() => setWithdrawing(false)}
          onWithdrawn={() => {
            setWithdrawing(false);
            void refreshAll();
          }}
        />
      ) : null}

      {assent !== null && version !== null ? (
        <RecordedAssentDialog
          mode={assent.mode}
          orgRef={org}
          consentId={consentId}
          boardMemberId={assent.boardMemberId}
          seatName={assent.seatName}
          contentHash={version.content_hash}
          consentDate={consent.consent_date ?? null}
          onClose={() => setAssent(null)}
          onRecorded={() => {
            setAssent(null);
            void refreshAll();
          }}
        />
      ) : null}
    </PageShell>
  );
}

/**
 * What the circulation's email did for one seat, as ONE quiet line under its role and address
 * (CBG-OPS1-CIRC-WEB, the frame `k2-roster`): when it was handed over, and "Hasn’t joined Board yet"
 * while the live seat is still unjoined; for any reason it didn't go, the officer's one act, in the
 * act tone; where no outcome was recorded, that Board can't say. Nothing for a seat with no delivery.
 *
 * The act is asked only while it can still be done: the consent out for signatures and this seat not
 * signed (`stillToSign`). Once the seat has signed, however that was recorded, or the consent was
 * withdrawn or executed, nothing is said in its place (the orchestrator's ruling on review-140's F1,
 * 2026-10-14): the line's own success path is the officer telling the director, who then signs. The
 * two facts, "Emailed …" and "We can’t confirm …", stay in every state.
 */
function DeliveryLine({
  seat,
  stillUnjoined,
  stillToSign,
}: {
  seat: BoardConsentRosterMemberOut;
  stillUnjoined: boolean;
  stillToSign: boolean;
}) {
  const outcome = seatDelivery(seat);
  if (outcome === null) return null;
  const testId = `consent-seat-delivery-${seat.board_member_id}`;
  if (outcome.state === 'not_sent') {
    if (!stillToSign) return null;
    return (
      <span
        className="font-sans text-s12 font-w600 tracking-givmo text-warning-700"
        data-testid={testId}
      >
        {circ.notSent}
      </span>
    );
  }
  return (
    <span
      className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary"
      data-testid={testId}
    >
      {outcome.state === 'sent'
        ? circ.emailedLine(displayDate(outcome.sentAt), outcome.unjoinedThen && stillUnjoined)
        : circ.notConfirmed}
    </span>
  );
}

/**
 * What a consent that names minutes approves (CBG-RC9), and — once it was executed — what happened to
 * them. The refusal goes first, where the officer will look, in the warning colour; an approval or an
 * approval another way is said quietly inside the card. A pin is promised only to a draft: one
 * withdrawn before it went out has none and never will.
 */
function ApprovesMinutes({ approves, draft }: { approves: ConsentMinutes; draft: boolean }) {
  const outcome = approves.outcome;
  const refused =
    outcome !== null && outcome !== 'approved' && outcome !== 'minutes_already_approved';
  const next = refused ? rc9.refusedNext(outcome) : null;
  const meetingId = approves.meetingId;
  // CBG-RC11 (§A7): a closed session's own minutes open on the minutes page at their own scope.
  const session = approves.closedSession?.segmentId ?? null;
  const openMinutes = (testId: string, className = LINK) =>
    meetingId === null ? null : (
      <Link
        to="/meetings/$meetingId/minutes"
        params={{ meetingId }}
        {...(session === null ? {} : { search: { session } })}
        className={className}
        data-testid={testId}
      >
        {rc9.openMinutes}
      </Link>
    );
  return (
    <>
      {refused ? (
        <Card testId="consent-detail-minutes-refused">
          <CardBody className="flex flex-col gap-8 border-l-4 border-warning-500">
            <span className="font-sans text-s15 font-w600 tracking-givmo text-warning-700">
              {rc9.refusedTitle}
            </span>
            <span className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
              {rc9.refusedWhy(outcome, approves.outcomeStatement)}
            </span>
            {next !== null ? (
              <span className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
                {next}
              </span>
            ) : null}
            {openMinutes('consent-detail-refused-open-minutes', `self-start ${LINK}`)}
          </CardBody>
        </Card>
      ) : null}

      <Card testId="consent-detail-approves">
        <CardBody className="flex flex-col gap-8">
          <h2 className="font-sans text-s15 font-w600 tracking-givmo text-text-strong">
            {rc9.whatItApproves}
          </h2>
          {approves.minutesTitle !== null ? (
            <span className="flex flex-wrap items-baseline gap-x-12 gap-y-4">
              <span className="font-sans text-s14 font-w500 tracking-givmo text-text-strong">
                {approves.minutesTitle}
              </span>
              {openMinutes('consent-detail-open-minutes')}
            </span>
          ) : outcome === 'minutes_unavailable' ? null : (
            // Gone before anyone signed (once executed, the refusal above says it).
            <span className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
              {rc9.minutesGone}
            </span>
          )}
          {outcome === 'approved' ? (
            <span
              className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary"
              data-testid="consent-detail-approved-them"
            >
              {rc9.approvedThem}
            </span>
          ) : null}
          {outcome === 'minutes_already_approved' ? (
            <span
              className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary"
              data-testid="consent-detail-already-approved"
            >
              {rc9.alreadyApproved}
            </span>
          ) : null}
          {approves.contentHash !== null ? (
            <div className="flex flex-col gap-6">
              <span className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
                {rc9.namedByFingerprint}
              </span>
              <Fingerprint
                value={approves.contentHash}
                label={rc9.minutesFingerprintLabel}
                testId="consent-detail-approves-hash"
              />
            </div>
          ) : draft ? (
            <span className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary">
              {rc9.pinnedWhenOut}
            </span>
          ) : null}
        </CardBody>
      </Card>
    </>
  );
}

/** Whether an executed consent's outcome for a set is a refusal (RC9: neither approved, nor already). */
function isRefusal(outcome: string | null): boolean {
  return outcome !== null && outcome !== 'approved' && outcome !== 'minutes_already_approved';
}

/** The way to a set's minutes page (CBG-RC11: a closed session's own at their own scope). */
function OpenMinutesLink({
  set,
  testId,
  className = LINK,
}: {
  set: ConsentMinutes;
  testId: string;
  className?: string;
}) {
  const meetingId = set.meetingId;
  if (meetingId === null) return null;
  const session = set.closedSession?.segmentId ?? null;
  return (
    <Link
      to="/meetings/$meetingId/minutes"
      params={{ meetingId }}
      {...(session === null ? {} : { search: { session } })}
      className={className}
      data-testid={testId}
    >
      {rc9.openMinutes}
    </Link>
  );
}

/**
 * CBG-RC9-MULTI: what a consent naming two or more sets approves, set by set, each with RC9's lines for
 * where it stands — named by its served title (a closed session's public one, with its general purpose
 * where two closed sessions would read alike), its fingerprint once pinned, and, once executed, what
 * the last signature did to it. A set it didn't approve leads, in the warning colour, with RC9's
 * refusal and the set's name, as RC9's one set does; the others are said quietly inside the card.
 */
function ApprovesSeveralSets({ sets, draft }: { sets: readonly ConsentMinutes[]; draft: boolean }) {
  const closedSets = closedSessionsAmong(sets);
  const named = (set: ConsentMinutes) =>
    set.minutesTitle === null
      ? null
      : set.closedSession !== null && closedSets > 1
        ? several.servedNameWithPurpose(set.minutesTitle, set)
        : set.minutesTitle;
  return (
    <>
      {sets.map((set, index) => {
        if (!isRefusal(set.outcome)) return null;
        const next = rc9.refusedNext(set.outcome);
        const name = named(set);
        return (
          <Card key={set.recordId} testId={`consent-detail-minutes-refused-${index}`}>
            <CardBody className="flex flex-col gap-8 border-l-4 border-warning-500">
              <span className="font-sans text-s15 font-w600 tracking-givmo text-warning-700">
                {rc9.refusedTitle}
              </span>
              {name !== null ? (
                <span className="font-sans text-s14 font-w500 tracking-givmo text-text-strong">
                  {name}
                </span>
              ) : null}
              <span className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
                {rc9.refusedWhy(set.outcome, set.outcomeStatement)}
              </span>
              {next !== null ? (
                <span className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
                  {next}
                </span>
              ) : null}
              <OpenMinutesLink
                set={set}
                testId={`consent-detail-refused-open-minutes-${index}`}
                className={`self-start ${LINK}`}
              />
            </CardBody>
          </Card>
        );
      })}

      <Card testId="consent-detail-approves">
        <CardBody className="flex flex-col gap-16">
          <h2 className="font-sans text-s15 font-w600 tracking-givmo text-text-strong">
            {rc9.whatItApproves}
          </h2>
          {sets.map((set, index) => {
            const name = named(set);
            return (
              <div
                key={set.recordId}
                className="flex flex-col gap-8"
                data-testid={`consent-detail-approves-set-${index}`}
              >
                {name !== null ? (
                  <span className="flex flex-wrap items-baseline gap-x-12 gap-y-4">
                    <span className="font-sans text-s14 font-w500 tracking-givmo text-text-strong">
                      {name}
                    </span>
                    <OpenMinutesLink set={set} testId={`consent-detail-open-minutes-${index}`} />
                  </span>
                ) : set.outcome === 'minutes_unavailable' ? null : (
                  // Gone before anyone signed (once executed, the refusal above says it).
                  <span className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
                    {rc9.minutesGone}
                  </span>
                )}
                {set.outcome === 'approved' ? (
                  <span
                    className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary"
                    data-testid={`consent-detail-approved-them-${index}`}
                  >
                    {rc9.approvedThem}
                  </span>
                ) : null}
                {set.outcome === 'minutes_already_approved' ? (
                  <span
                    className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary"
                    data-testid={`consent-detail-already-approved-${index}`}
                  >
                    {rc9.alreadyApproved}
                  </span>
                ) : null}
                {set.contentHash !== null ? (
                  <div className="flex flex-col gap-6">
                    <span className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
                      {rc9.namedByFingerprint}
                    </span>
                    <Fingerprint
                      value={set.contentHash}
                      label={rc9.minutesFingerprintLabel}
                      testId={`consent-detail-approves-hash-${index}`}
                    />
                  </div>
                ) : null}
              </div>
            );
          })}
          {draft && sets.every((set) => set.contentHash === null) ? (
            <span className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary">
              {rc9.pinnedWhenOut}
            </span>
          ) : null}
        </CardBody>
      </Card>
    </>
  );
}

/**
 * Editing a draft's text in place.
 *
 * Only reachable while the consent is a draft: from circulation onward the version's text is frozen at
 * the database, and a change is a REVISION with everything that costs. The two are deliberately
 * different controls with different words, so an officer cannot reach the expensive one by habit.
 */
function DraftEditor({
  orgRef,
  consentId,
  title: initialTitle,
  body: initialBody,
  consentDate: initialDate,
  onCancel,
  onSaved,
}: {
  orgRef: string;
  consentId: string;
  title: string;
  body: string;
  consentDate: string;
  onCancel: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const operations = useBoardOperations();
  const [title, setTitle] = useState(initialTitle);
  const [body, setBody] = useState(initialBody);
  const [consentDate, setConsentDate] = useState(initialDate);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      await operations.officerUpdateBoardWrittenConsent({
        path: { org_ref: orgRef, consent_id: consentId },
        body: { title: title.trim(), body, consent_date: consentDate },
        throwOnError: true,
      });
    },
    onSuccess: async () => {
      toast.success('Draft saved.');
      await onSaved();
    },
    onError: (caught) =>
      setError(isGivmoError(caught) ? caught.message : 'We couldn’t save that. Please try again.'),
  });

  return (
    <Card testId="consent-detail-editor">
      <CardBody className="flex flex-col gap-12">
        <TextField
          label="Title"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          testId="consent-edit-title"
        />
        <TextField
          label="Date this consent bears"
          type="date"
          value={consentDate}
          onChange={(event) => setConsentDate(event.target.value)}
          testId="consent-edit-date"
        />
        <Textarea
          label="What the consent says"
          rows={14}
          value={body}
          onChange={(event) => setBody(event.target.value)}
          testId="consent-edit-body"
        />

        {error !== null ? <ErrorMessage testId="consent-edit-error">{error}</ErrorMessage> : null}

        <div className="flex justify-end gap-10">
          <Button
            appearance="saas"
            tone="secondary"
            disabled={save.isPending}
            onClick={onCancel}
            testId="consent-edit-cancel"
          >
            Cancel
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            loading={save.isPending}
            disabled={title.trim().length === 0}
            onClick={() => {
              setError(null);
              save.mutate();
            }}
            testId="consent-edit-save"
          >
            Save the draft
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}
